import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, lastValueFrom } from "rxjs";
import { HttpLink } from "apollo-angular/http";
import { ActionsSubject, Store } from "@ngrx/store";
import { ErrorCodeService } from "./error-code.service";
import { ApolloClientOptions, ApolloLink, fromPromise, InMemoryCache, Operation, DefaultOptions } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { selectAuthCredentials } from "../../features/auth/store/auth.selectors";
import { take } from "rxjs/operators";
import { onError } from "@apollo/client/link/error";
import { logout, refreshToken, refreshTokenFailure, refreshTokenSuccess } from "../../features/auth/store/auth.actions";
import { Actions, ofType } from "@ngrx/effects";
import { GraphQLError } from "graphql/error";
import { environment } from "../../../environments/environment";
import { generateRequestId, getOperationRequestId, getRequestId, REQUEST_ID_CONTEXT_KEY, REQUEST_ID_HEADER } from "../graphql/request-id";
import { redactSensitiveValues, reportErrorForSupport } from "../sentry/error-reporting";

@Injectable({
  providedIn: 'root'
})
export class ApolloService {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  private whitelistedGraphqlRequests: string[] = ['authRefreshToken'];

  private refreshTokenCompleted$ = this.updates$.pipe(ofType(refreshTokenSuccess, refreshTokenFailure));

  constructor(
    private httpLink: HttpLink,
    private store: Store,
    private errorCodeService: ErrorCodeService,
    private updates$: ActionsSubject
  ) {
  }

  private addTokenToRequest(operation: Operation, token: string): Operation {
    operation.setContext({
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return operation;
  }

  private createAuthLink() {
    return setContext(async (operation, context) => {
      const tokenPair = await firstValueFrom(this.store.select(selectAuthCredentials));

      if ('headers' in context && 'Authorization' in context['headers']) return context;
      if (operation.operationName && this.whitelistedGraphqlRequests.includes(operation.operationName)) return context;
      if (!tokenPair) return context;

      return {...context, headers: {...context['headers'], Authorization: `Bearer ${tokenPair.accessToken}`}};
    });
  }

  /**
   * Stamps every outbound operation with a request id and keeps it reachable afterwards.
   *
   * The id is the short reference a user reads off an error message (`WEB-7K2M-9F4T`) and quotes to
   * support, who resolves it against `graphql_request_log` on the server. Minted here rather than
   * server-side so one exists even when the request never arrives — offline, DNS, an edge 5xx —
   * which is precisely the failure with no server-side trace to find.
   *
   * Deliberately no `Sentry.setTag` here: a tag set on the global scope is overwritten by the next
   * operation, so a concurrently-captured event would carry whichever request started most recently.
   * A confidently wrong reference is worse than none. `reportGraphQLErrorToSentry` tags the id it
   * actually holds instead.
   */
  private createRequestIdLink() {
    return new ApolloLink((operation, forward) => {
      const requestId = generateRequestId();

      operation.setContext((context: Record<string, any>) => ({
        ...context,
        [REQUEST_ID_CONTEXT_KEY]: requestId,
        headers: { ...context['headers'], [REQUEST_ID_HEADER]: requestId },
      }));

      return forward(operation);
    });
  }

  private createErrorLink() {
    return onError(({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        for (const graphQLError of graphQLErrors) {
          const { message, locations, path, extensions } = graphQLError;
          console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

          this.reportGraphQLErrorToSentry(graphQLError, operation);

          const currentAccessToken = this.getCurrentAccessTokenFromOperation(operation);

          if (extensions['code'] === 'UNAUTHENTICATED' && !this.whitelistedGraphqlRequests.includes(operation.operationName) && currentAccessToken) {
            if (!this.isRefreshing) {
              this.isRefreshing = true;
              this.refreshTokenSubject.next(null);

              this.store.dispatch(refreshToken());

              return fromPromise(
                firstValueFrom(this.refreshTokenCompleted$).then(async (result) => {
                  if (result.type === refreshTokenFailure.type) {
                    console.error('Error refreshing token', result.error);
                    this.store.dispatch(logout());
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(null);

                    return forward(operation);
                  }

                  const refreshedTokenPair = result.credentials;

                  if (!refreshedTokenPair || refreshedTokenPair.accessToken == currentAccessToken) {
                    console.error('No refreshed access token returned');
                    this.store.dispatch(logout());
                    throw Error('No refreshed access token returned');
                  }
                  this.isRefreshing = false;
                  this.refreshTokenSubject.next(refreshedTokenPair.accessToken);

                  return forward(this.addTokenToRequest(operation, refreshedTokenPair.accessToken));
                })
              ).flatMap(() => forward(operation));
            } else {
              return fromPromise(
                new Promise(resolve => {
                  this.refreshTokenSubject.subscribe(token => {
                    if (token) {
                      resolve(this.addTokenToRequest(operation, token));
                    }
                  });
                })
              ).flatMap(() => forward(operation));
            }
          }
        }
      }

      if (networkError) {
        // No response arrived, so there is no `extensions.requestId` to read later. Carry the id we
        // minted on the error itself, which is what lets the toast still show the user a reference.
        const requestId = getOperationRequestId(operation);
        if (requestId) Object.assign(networkError, { requestId });

        // Reported to Sentry by httpClientIntegration / graphqlClientIntegration.
        console.debug(`[Network error]: ${networkError}`);
      }

      // Deliberately nothing returned: in `onError`, returning an observable replaces the result,
      // so a `forward(operation)` here would re-send every failed operation exactly once. That
      // retry is why a failed "send verification code" came back as CODE_RESEND_REQUEST_TOO_FAST —
      // the second attempt hit the API's own rate limit and its error is what the user saw, hiding
      // the INTERNAL_SERVER_ERROR that actually failed. Only the token-refresh branches above
      // retry, which is the one case where the retry can succeed.
      return;
    });
  }

  /**
   * Reports a GraphQL error to Sentry. Errors without a code or with an INTERNAL_SERVER_ERROR
   * code are unexpected and reported as exceptions, all other codes are expected business
   * errors (validation, permissions, ...) and are only reported as messages.
   * UNAUTHENTICATED is skipped entirely, it is handled by the token refresh in the error link.
   */
  private reportGraphQLErrorToSentry(error: GraphQLError, operation: Operation): void {
    const code = error.extensions?.['code'] as string | undefined;

    if (code === 'UNAUTHENTICATED') return;

    // An outcome the user is told about and can act on is not a defect — see `isExpectedError`.
    if (this.errorCodeService.isExpectedError(error)) return;

    reportErrorForSupport(error, {
      // The error may not carry the id back, but the operation always knows the one it was sent
      // with. Reporting under it makes `request_id:WEB-…` return this event and the server's row.
      reference: getRequestId(error) ?? getOperationRequestId(operation),
      // Expected, handled failures should not read as crashes.
      asMessage: !!code && code !== 'INTERNAL_SERVER_ERROR',
      tags: { surface: 'graphql' },
      context: {
        operation: operation.operationName,
        // Redacted: an OAuth `idToken` or a verification code in here would be a credential
        // sitting in a third-party error report.
        operationVariables: JSON.stringify(redactSensitiveValues(operation.variables)),
        code: code,
      },
    });
  }

  private getCurrentAccessTokenFromOperation(operation: Operation): string | undefined {
    if (!operation.getContext()['headers']) return undefined;

    const authHeader = operation.getContext()['headers'].Authorization;
    if (!authHeader) return undefined;
    return authHeader.split(' ')[1];
  }

  private createFormatErrorLink() {
    return  new ApolloLink((operation, forward) => {
      return forward(operation).map(response => {
        // Flatten the error to give detailed error message
        // Note: for admin only
        if (response.errors) {
          const errorCode = this.errorCodeService.getErrorCode(response.errors[0] as GraphQLError);
          let message = this.errorCodeService.getErrorMessageFromCode(errorCode);
          response.errors[0].message = message;
        }
        return response;
      });
    });
  }

  private defaultOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
    mutate: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    }
  }

  createApollo(): ApolloClientOptions<any> {
    const authLink = this.createAuthLink();
    const formatErrorLink = this.createFormatErrorLink();
    const errorLink = this.createErrorLink();
    const httpLink = this.httpLink.create({ uri: environment.serverUrl + environment.graphQLEndpoint });

    const requestIdLink = this.createRequestIdLink();

    // After the error link, so the operation it replays on an expired token is a genuinely new
    // request and gets its own id.
    const link = ApolloLink.from([formatErrorLink, errorLink, requestIdLink, authLink, httpLink]);

    const cache = new InMemoryCache();

    return {
      link,
      cache,
      defaultOptions: this.defaultOptions
    };
  }
}
